/**
 * Vocabulario compartido de accesos: roles dentro de la empresa y presentación
 * del catálogo de permisos.
 *
 * Las etiquetas de los módulos y la agrupación vivían dentro de `admin/roles`,
 * así que la ficha del usuario no podía pintar la misma matriz sin copiarlas.
 * Aquí las comparten la pantalla de roles y la de usuarios: un permiso nuevo se
 * etiqueta en un único sitio.
 */
import type { ChipColorType } from '~/types/Chip'
import type { PermissionType } from './permission.service'
import type { CompanyRoleType } from './user.service'

export const COMPANY_ROLE_OPTIONS: Array<{ value: CompanyRoleType; title: string; subtitle: string }> = [
  { value: 'member', title: 'Miembro', subtitle: 'Acceso según sus permisos' },
  { value: 'admin', title: 'Administrador', subtitle: 'Puede gestionar usuarios de la empresa' },
  { value: 'owner', title: 'Propietario', subtitle: 'Control total: se salta los permisos' }
]

export const companyRoleLabel = (role?: string | null): string => COMPANY_ROLE_OPTIONS.find(o => o.value === role)?.title ?? 'Sin rol'

export const companyRoleColor = (role?: string | null): ChipColorType => (role === 'owner' ? 'danger' : role === 'admin' ? 'warning' : 'default')

/** Iniciales para el avatar cuando no hay imagen: "Ana Pérez" → "AP". */
export const userInitials = (name?: string | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase()
}

/** Módulos con CRUD completo (read/create/update/delete). */
export const CRUD_MODULES: Record<string, string> = {
  customers: 'Clientes',
  products: 'Catálogo',
  inventory: 'Inventario',
  quotes: 'Cotizaciones',
  payment_accounts: 'Cuentas de cobro',
  forms: 'Formularios',
  pages: 'Páginas',
  parameters: 'Parámetros',
  users: 'Usuarios',
  roles: 'Roles',
  permissions: 'Permisos',
  companies: 'Empresas'
}

/** Permisos especiales (no siguen el patrón CRUD). */
export const SPECIAL_PERMISSIONS: Record<string, string> = {
  'ai.chat': 'Chat IA',
  'ai.agents.create': 'Crear agentes IA',
  'ai.agents.read': 'Ver agentes IA',
  'ai.agents.update': 'Editar agentes IA',
  'ai.agents.delete': 'Eliminar agentes IA',
  'leads.read': 'Ver prospectos',
  'leads.update': 'Editar prospectos',
  'leads.delete': 'Eliminar prospectos',
  'crm.read': 'Ver el CRM',
  'builder.*': 'Builder (componentes/modelos/temas)',
  'users.assign': 'Asignar roles/permisos',
  '*': 'Acceso total (superadmin)'
}

export const CRUD_ACTION_LABELS: Record<string, string> = { read: 'Ver', create: 'Crear', update: 'Editar', delete: 'Eliminar' }

/** Etiqueta legible de un permiso suelto. */
export const permissionLabel = (permission: PermissionType): string => SPECIAL_PERMISSIONS[permission.name] ?? permission.description ?? permission.name

export interface PermissionGroupType {
  module: string
  label: string
  permissions: PermissionType[]
}

/**
 * Agrupa los permisos por módulo para pintar la matriz: primero los módulos con
 * CRUD, en el orden del catálogo, y después los especiales.
 *
 * Los permisos sólo de super admin (`ai.config.*`, `mail.*`) se quedan fuera:
 * asignarlos desde aquí no otorgaría nada porque el backend exige `is_root`.
 */
export const groupPermissions = (permissions: PermissionType[]): PermissionGroupType[] => {
  const groups = new Map<string, PermissionType[]>()

  for (const p of permissions) {
    if (p.name.startsWith('ai.config.') || p.name.startsWith('mail.')) continue

    const dotIndex = p.name.indexOf('.')
    const module = dotIndex > 0 ? p.name.slice(0, dotIndex) : p.name
    // `payment_accounts.read` cae en el módulo `payment_accounts`, no en `payment`.
    const key = Object.keys(CRUD_MODULES).find(m => p.name.startsWith(`${m}.`)) ?? module

    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(p)
  }

  const result: PermissionGroupType[] = []
  for (const [module, label] of Object.entries(CRUD_MODULES)) {
    const perms = groups.get(module)
    if (perms && perms.length > 0) {
      result.push({ module, label, permissions: perms })
      groups.delete(module)
    }
  }
  for (const [module, perms] of groups) {
    result.push({ module, label: module, permissions: perms })
  }
  return result
}
